import is from '@sindresorhus/is';
import type { UseMutationResult, UseQueryOptions, UseQueryResult } from '@tanstack/react-query';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useContext } from 'react';

import { ApplicationContext } from '~/shared/context/context';
import type {
    BookmarkedLearningOpportunity,
    UserLearningOpportunitiesBookmarksOptions,
} from '~/shared/data-contract/learning-opportunities';
import {
    bookmarkedLearningOpportunityListSchema,
    userLearningOpportunitiesBookmarksOptionsSchema,
} from '~/shared/data-contract/learning-opportunities';
import type { Pathname } from '~/shared/http-fetcher/http-fetcher';

import { useSnackbars } from '../../error-handling/SnackBars';

export const useUserBookmarksPermission = (
    userId: string,
): UseQueryResult<UserLearningOpportunitiesBookmarksOptions> => {
    const pathname: Pathname = `/users/${userId}/bookmarks/options`;

    const { validatingCobrainerApiFetcher } = useContext(ApplicationContext);

    return useQuery({
        enabled: is.nonEmptyString(userId),
        queryFn: async () => {
            const result = await validatingCobrainerApiFetcher.get(userLearningOpportunitiesBookmarksOptionsSchema, {
                pathname,
            });
            if (result.isOk) {
                return result.value;
            }

            throw result.error;
        },
        queryKey: [pathname, 'GET'],
    });
};

export const useUserBookmarks = (
    userId: string,
    options?: Pick<UseQueryOptions<readonly BookmarkedLearningOpportunity[]>, 'select'>,
): UseQueryResult<readonly BookmarkedLearningOpportunity[]> => {
    const pathname: `/${string}` = `/users/${userId}/bookmarks`;

    const { validatingCobrainerApiFetcher } = useContext(ApplicationContext);
    const { data: permissions } = useUserBookmarksPermission(userId);

    return useQuery({
        enabled: is.nonEmptyString(userId) && permissions?.canReadBookmarks === true,
        queryFn: async () => {
            const result = await validatingCobrainerApiFetcher.get(bookmarkedLearningOpportunityListSchema, {
                pathname,
            });
            if (result.isOk) {
                return result.value;
            }

            throw result.error;
        },
        queryKey: [pathname, 'get'],
        ...options,
    });
};

export const useAddUserBookmark = (userId: string | undefined): UseMutationResult<void, Error, string> => {
    const { cobrainerApiFetcher } = useContext(ApplicationContext);
    const queryClient = useQueryClient();
    const { showErrorSnackbar } = useSnackbars();

    return useMutation({
        mutationFn: async (learningOpportunityId: string) => {
            const result = await cobrainerApiFetcher.post({
                pathname: `/users/${userId}/bookmarks/${learningOpportunityId}`,
            });
            if (result.isOk) {
                return;
            }

            throw result.error;
        },
        onError: (error) => {
            showErrorSnackbar(error);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [`/users/${userId}/bookmarks`] });
        },
    });
};

export const useRemoveUserBookmark = (userId: string | undefined): UseMutationResult<void, Error, string> => {
    const { cobrainerApiFetcher } = useContext(ApplicationContext);
    const queryClient = useQueryClient();
    const { showErrorSnackbar } = useSnackbars();

    return useMutation({
        mutationFn: async (learningOpportunityId: string) => {
            const result = await cobrainerApiFetcher.delete({
                pathname: `/users/${userId}/bookmarks/${learningOpportunityId}`,
            });
            if (result.isOk) {
                return;
            }

            throw result.error;
        },
        onError: (error) => {
            showErrorSnackbar(error);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [`/users/${userId}/bookmarks`] });
        },
    });
};

export const useUserBookmarksFilterByTitle = (
    userId: string,
    title: string | undefined,
): UseQueryResult<readonly BookmarkedLearningOpportunity[]> =>
    useUserBookmarks(userId, {
        select: (data) => {
            if (!is.nonEmptyArray(data)) {
                return [];
            }
            if (is.nonEmptyString(title)) {
                return data?.filter((bookmark) => bookmark.title.toLowerCase().includes(title.toLowerCase()));
            }

            return data;
        },
    });
